import orderBy from 'lodash/orderBy';
import takeRight from 'lodash/takeRight';
import sumBy from 'lodash/sumBy';
import meanBy from 'lodash/meanBy';
import filter from 'lodash/filter';
import round from 'lodash/round';

import { NormalizedGameData } from '../data_types/types';
import {
    DIVISION_PROSPECT_THRESHOLD,
    DIVISION_EXPERT_THRESHOLD,
    DIVISION_CHAMPION_THRESHOLD,
} from './const';

/**
 * Name for the current streak. It is strictly up/down, or "no drop"/"no gain"
 * when it includes rounds with no rating change.
 */
export const describeCurrentStreak = (pointsGained: number, includesZero?: boolean) => {
    if (pointsGained >= 0) return includesZero ? 'No drop' : 'Climbing';
    return includesZero ? 'No gain' : 'Sliding';
};

export const DIVISION_NAMES = ['Rookie', 'Prospect', 'Expert', 'Champion'];
export const DIVISION_THRESHOLDS = [0, DIVISION_PROSPECT_THRESHOLD, DIVISION_EXPERT_THRESHOLD, DIVISION_CHAMPION_THRESHOLD];

/**
 * History ordered oldest -> newest. Sorted defensively so callers never depend
 * on the order the context happens to hold.
 */
export const chronological = (historyData: NormalizedGameData[]): NormalizedGameData[] =>
    orderBy(historyData, 'matchNum', 'asc');

/**
 * Numeric finishing place, or null for DNF / retired rounds.
 */
export const placeNumber = (position: string): number | null => {
    const n = parseInt(String(position).replace('T', ''), 10);
    return Number.isNaN(n) ? null : n;
};

export const placeLabel = (position: string) => {
    const n = placeNumber(position);
    if (n === null) return position === 'R' ? 'RET' : 'DNF';
    const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
    return `${String(position).startsWith('T') ? 'T' : ''}${n}${suffix}`;
};

export type PlacementBucket = { place: 1 | 2 | 3 | 4 | 0; count: number; tied: number; percent: number };

export type PlacementSummary = {
    total: number;
    buckets: PlacementBucket[]; // 1st, 2nd, 3rd, 4th, then 0 = DNF / retired
    winPercent: number;
    averagePlace: number;
};

export const getPlacementSummary = (historyData: NormalizedGameData[]): PlacementSummary => {
    const total = historyData.length;
    const buckets: PlacementBucket[] = ([1, 2, 3, 4, 0] as const).map(place => ({ place, count: 0, tied: 0, percent: 0 }));

    historyData.forEach(({ position }) => {
        const n = placeNumber(position);
        const bucket = buckets.find(b => b.place === (n ?? 0)) ?? buckets[4];
        bucket.count += 1;
        if (String(position).startsWith('T')) bucket.tied += 1;
    });

    buckets.forEach(b => { b.percent = total > 0 ? round((b.count / total) * 100) : 0; });

    const finished = filter(historyData, d => placeNumber(d.position) !== null);

    return {
        total,
        buckets,
        winPercent: buckets[0].percent,
        averagePlace: finished.length ? round(meanBy(finished, d => placeNumber(d.position) as number), 2) : 0,
    };
};

/**
 * Card record: W = outright 1st, T = tied for 1st, L = anything else (incl. RET / DNF).
 */
export const getRecord = (rounds: NormalizedGameData[]) => {
    const firsts = filter(rounds, d => placeNumber(d.position) === 1);
    const t = filter(firsts, d => String(d.position).startsWith('T')).length;
    return { w: firsts.length - t, t, l: rounds.length - firsts.length };
};

export type RecentForm = {
    rounds: NormalizedGameData[];
    ratingChange: number;
    record: { w: number; t: number; l: number };
    placements: PlacementBucket[];
    averageChange: number;
};

/**
 * Net rating change over the most recent `count` rounds.
 */
export const getRecentRatingChange = (historyData: NormalizedGameData[], count: number) =>
    sumBy(takeRight(chronological(historyData), count), 'ratingChange');

export const getRecentForm = (historyData: NormalizedGameData[], count = 10): RecentForm => {
    const rounds = takeRight(chronological(historyData), count);
    return {
        rounds,
        ratingChange: sumBy(rounds, 'ratingChange'),
        record: getRecord(rounds),
        placements: getPlacementSummary(rounds).buckets,
        averageChange: rounds.length ? round(meanBy(rounds, 'ratingChange'), 1) : 0,
    };
};

/**
 * Average size of a rating gain vs a rating loss. Shows whether a player's
 * good rounds are worth more than their bad rounds cost.
 */
export const getSwingSizes = (historyData: NormalizedGameData[]) => {
    const gains = filter(historyData, d => d.ratingChange > 0);
    const losses = filter(historyData, d => d.ratingChange < 0);
    return {
        averageGain: gains.length ? round(meanBy(gains, 'ratingChange'), 1) : 0,
        averageLoss: losses.length ? round(meanBy(losses, 'ratingChange'), 1) : 0,
    };
};

/**
 * Progress from the current division's rating threshold toward the next one.
 * Returns null when the player is in the top division or unranked.
 */
export const getDivisionProgress = (division: number, rating: number) => {
    if (division < 0 || division >= DIVISION_THRESHOLDS.length - 1) return null;
    const from = DIVISION_THRESHOLDS[division];
    const to = DIVISION_THRESHOLDS[division + 1];
    return {
        nextName: DIVISION_NAMES[division + 1],
        threshold: to,
        remaining: Math.max(0, to - rating),
        progress: Math.max(0, Math.min((rating - from) / (to - from), 1)),
    };
};
